import { gql } from "@apollo/client";
import FormSection from "controls/FormSection";
import FormText from "controls/FormText";
import MultiValuesField from "controls/MultiValuesField";
import useIsAdmin from "hooks/useIsAdmin";
import NumberInput from "inputs/NumberInput";
import SwitchInput from "inputs/SwitchInput";
import TextInput from "inputs/TextInput";
import React, { useState } from "react";
import ReadminObjectInput from "readmin_pages/shared/ReadminObjectInput";

export const SurfacesFormFragment = gql`
  fragment SurfacesFormFragment on Surface {
    id
    name
    canary
    scaleInput
    requireManualReview
    encodeAudio
    motioncropEnabled
    aiStatsCapable
    ballTrackingProcessNthFrame
    videoBitrate720Kbps
    videoBitrate1080Kbps
    camera {
      id
    }
  }
`;

export default function SurfacesForm({ surfaces }) {
  const isAdmin = useIsAdmin();
  surfaces = surfaces.map((surface) => ({
    scaleInput: true,
    motioncropEnabled: true,
    aiStatsCapable: true,
    ballTrackingProcessNthFrame: 10,
    ...surface,
  }));

  const [motioncropEnabledValues, setMotioncropEnabledValues] = useState(
    surfaces.map((surface) => surface.motioncropEnabled),
  );
  const motioncropDisabled = motioncropEnabledValues.every((v) => v === false);

  return (
    <>
      <FormText uiStringKey="readmin.surface.description" />
      <MultiValuesField
        label="Name"
        name="name"
        required
        defaultValues={surfaces?.map((surface) => surface.name)}
        input={<TextInput />}
        helperTextUiStringKey="readmin.surface.fields.name"
      />
      <MultiValuesField
        label="Camera"
        name="camera"
        required
        defaultValues={surfaces?.map((surface) => surface.camera)}
        input={<ReadminObjectInput optionsTypename="Camera" />}
        helperTextUiStringKey="readmin.surface.fields.camera"
      />
      <MultiValuesField
        label="Canary"
        name="canary"
        defaultValues={surfaces?.map((surface) => surface.canary)}
        input={<SwitchInput />}
        helperTextUiStringKey="readmin.surface.fields.canary"
      />
      <MultiValuesField
        label="Require Manual Review"
        name="requireManualReview"
        defaultValues={surfaces?.map((surface) => surface.requireManualReview)}
        input={<SwitchInput />}
        helperTextUiStringKey="readmin.surface.fields.require_manual_review"
      />
      <MultiValuesField
        label="Scale Input"
        name="scaleInput"
        defaultValues={surfaces?.map((surface) => surface.scaleInput)}
        input={<SwitchInput />}
        helperTextUiStringKey="readmin.surface.fields.scale_input"
      />
      <MultiValuesField
        label="Encode Audio"
        name="encodeAudio"
        defaultValues={surfaces?.map((surface) => surface.encodeAudio)}
        input={<SwitchInput />}
        helperTextUiStringKey="readmin.surface.fields.encode_audio"
      />
      {isAdmin && (
        <MultiValuesField
          label="AI Stats Capable"
          name="aiStatsCapable"
          defaultValues={surfaces?.map((surface) => surface.aiStatsCapable)}
          input={<SwitchInput />}
          helperTextUiStringKey="readmin.surface.fields.ai_stats_capable"
        />
      )}

      <FormSection header="Motioncrop Settings" defaultExpanded>
        <MultiValuesField
          label="Enabled"
          name="motioncropEnabled"
          defaultValues={surfaces?.map((surface) => surface.motioncropEnabled)}
          onChange={setMotioncropEnabledValues}
          input={<SwitchInput />}
          helperTextUiStringKey="readmin.surface.fields.motioncrop_enabled"
        />
        <div style={{ marginTop: 5 }} />
        <MultiValuesField
          label="Ball Tracking Process Nth Frame"
          name="ballTrackingProcessNthFrame"
          defaultValues={surfaces?.map((surface) => surface.ballTrackingProcessNthFrame)}
          disabled={motioncropDisabled}
          input={<NumberInput min={5} max={50} />}
          helperTextUiStringKey="readmin.surface.fields.ball_tracking_process_nth_frame"
        />
        <div style={{ marginTop: 5 }} />
        <MultiValuesField
          label="720p Bitrate"
          name="videoBitrate720Kbps"
          defaultValues={surfaces?.map((surface) => surface.videoBitrate720Kbps)}
          disabled={motioncropDisabled}
          input={<NumberInput unit="kbps" min={2000} max={4000} step={100} initValue={2000} />}
          helperTextUiStringKey="readmin.surface.fields.video_bitrate_720_kbps"
        />
        <div style={{ marginTop: 5 }} />
        <MultiValuesField
          label="1080p Bitrate"
          name="videoBitrate1080Kbps"
          defaultValues={surfaces?.map((surface) => surface.videoBitrate1080Kbps)}
          disabled={motioncropDisabled}
          input={<NumberInput unit="kbps" min={4000} max={6000} step={100} initValue={4000} />}
          helperTextUiStringKey="readmin.surface.fields.video_bitrate_1080_kbps"
        />
      </FormSection>
    </>
  );
}
